import React, { useState } from 'react';
import { ArrowLeft, MapPin, Calendar, Thermometer, CloudRain, Sun } from 'lucide-react';
import { myLocationApi, LocationWeatherPatterns } from '../services/myLocationApi';

interface MyLocationFlowProps {
  onBack: () => void;
  onLoading: (loading: boolean) => void;
  onError: (error: string | null) => void;
}

// Use the LocationWeatherPatterns interface from the API service

export const MyLocationFlow: React.FC<MyLocationFlowProps> = ({ onBack, onLoading, onError }) => {
  const [step, setStep] = useState<'permission' | 'loading' | 'results'>('permission');
  const [locationData, setLocationData] = useState<LocationWeatherPatterns | null>(null);

  const requestLocation = () => {
    setStep('loading');
    onLoading(true);

    if (!navigator.geolocation) {
      onError('Geolocation is not supported by this browser');
      setStep('permission');
      onLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await myLocationApi.getWeatherPatterns(
            position.coords.latitude, 
            position.coords.longitude
          );
          
          setLocationData(response.data);
          setStep('results');
        } catch (error) {
          onError(error instanceof Error ? error.message : 'Failed to get weather data for your location');
          setStep('permission');
        } finally {
          onLoading(false);
        }
      },
      (error) => {
        let errorMessage = 'Failed to get your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions and try again.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
        onError(errorMessage);
        setStep('permission');
        onLoading(false);
      }
    );
  };

  if (step === 'results' && locationData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <MapPin className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Weather at {locationData.location}</h2>
          </div>
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
        </div>

        <div className="space-y-6">
          {/* Today's Typical Weather */}
          <div className="card bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-purple-200 dark:border-purple-800">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-purple-600" />
              What's typical for today ({new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })})
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <Thermometer className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {locationData.todayTypical.averageHigh}°F
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    High (Low: {locationData.todayTypical.averageLow}°F)
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <CloudRain className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {locationData.todayTypical.rainChance}%
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Chance of precipitation
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <Sun className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Typical Conditions
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {locationData.todayTypical.typicalConditions}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 7-Day Historical Pattern */}
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Typical weather pattern for this week (±7 days)
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Date</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600 dark:text-gray-400">High</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Low</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Precipitation Chance</th>
                  </tr>
                </thead>
                <tbody>
                  {locationData.weekPattern.map((day) => {
                    // Find today by checking if this date matches today's date
                    const today = new Date();
                    const dayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const isToday = day.date === dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    
                    return (
                      <tr key={day.date} className={`border-b border-gray-100 dark:border-gray-800 ${isToday ? 'bg-purple-50 dark:bg-purple-900/20' : ''}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            {isToday && <Calendar className="h-4 w-4 text-purple-600" />}
                            <span className={`font-medium ${isToday ? 'text-purple-600 dark:text-purple-400' : 'text-gray-900 dark:text-white'}`}>
                              {day.date}
                              {isToday && ' (Today)'}
                            </span>
                          </div>
                        </td>
                      <td className="py-3 px-4 text-center text-gray-900 dark:text-white font-medium">
                        {day.averageHigh}°F
                      </td>
                      <td className="py-3 px-4 text-center text-gray-600 dark:text-gray-400">
                        {day.averageLow}°F
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          day.rainChance < 20 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : day.rainChance < 40
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {day.rainChance}%
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Year-Round Summary */}
          <div className="card bg-gray-50 dark:bg-gray-800/50">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Your location's climate year-round
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {locationData.yearRoundSummary}
            </p>
          </div>

          <div className="card border-l-4 border-blue-500">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">About this data:</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              This information is based on 20+ years of historical weather data from the nearest NOAA weather station 
              to your location. The "typical" conditions show what you can generally expect during this time of year, 
              though actual daily weather may vary.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <MapPin className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Location</h2>
        </div>
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
      </div>

      <div className="card text-center">
        <div className="w-24 h-24 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <MapPin className="h-12 w-12 text-white" />
        </div>
        
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Discover your local weather patterns
        </h3>
        
        <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-lg mx-auto">
          We'll use your location to show you what the weather is typically like where you are, 
          including historical patterns for this time of year based on 20+ years of data.
        </p>
        
        <button
          onClick={requestLocation}
          className="btn-primary"
        >
          Share My Location
        </button>
        
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 text-sm">What you'll see:</h4>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 text-left">
            <li>• Typical weather for today's date historically</li>
            <li>• 7-day pattern around this date (±7 days)</li>
            <li>• Year-round climate summary for your area</li>
            <li>• Data from the nearest weather station</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
